import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/SEOHead";

type Status = "loading" | "valid" | "already_unsubscribed" | "invalid" | "success" | "error";

const EmailUnsubscribe = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [status, setStatus] = useState<Status>("loading");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      return;
    }

    const validate = async () => {
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
        const res = await fetch(
          `${supabaseUrl}/functions/v1/handle-email-unsubscribe?token=${token}`,
          { headers: { apikey: anonKey } }
        );
        const data = await res.json();
        if (data.valid === false && data.reason === "already_unsubscribed") {
          setStatus("already_unsubscribed");
        } else if (data.valid) {
          setStatus("valid");
        } else {
          setStatus("invalid");
        }
      } catch {
        setStatus("error");
      }
    };
    validate();
  }, [token]);

  const handleUnsubscribe = async () => {
    if (!token) return;
    setSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke("handle-email-unsubscribe", {
        body: { token },
      });
      if (error) throw error;
      setStatus("success");
    } catch {
      setStatus("error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead title="Unsubscribe — Dearestly" description="Manage your email preferences" />
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="w-full max-w-md rounded-2xl bg-card p-8 shadow-sm text-center">
          <h1 className="mb-2 font-serif text-2xl font-semibold text-foreground">Email Preferences</h1>

          {status === "loading" && (
            <p className="text-sm text-muted-foreground">Validating your request…</p>
          )}

          {status === "valid" && (
            <>
              <p className="mb-6 text-sm text-muted-foreground">
                Click below to unsubscribe from app emails. You'll still receive essential account emails.
              </p>
              <Button
                onClick={handleUnsubscribe}
                disabled={submitting}
                className="w-full rounded-full"
                size="lg"
              >
                {submitting ? "Processing…" : "Confirm Unsubscribe"}
              </Button>
            </>
          )}

          {status === "already_unsubscribed" && (
            <p className="text-sm text-muted-foreground">You've already been unsubscribed from these emails.</p>
          )}

          {status === "success" && (
            <p className="text-sm text-muted-foreground">You've been unsubscribed. You won't receive these emails anymore.</p>
          )}

          {status === "invalid" && (
            <p className="text-sm text-destructive">This unsubscribe link is invalid or has expired.</p>
          )}

          {status === "error" && (
            <p className="text-sm text-destructive">Something went wrong. Please try again later.</p>
          )}
        </div>
      </div>
    </>
  );
};

export default EmailUnsubscribe;
